import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/cn';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './DropdownMenu';

export interface RowAction {
  label: string;
  onSelect: () => void;
  /** Destructive — red, and always listed after the safe actions behind a separator. */
  danger?: boolean;
  disabled?: boolean;
  /** Why a disabled action is disabled, shown under its label. */
  hint?: string;
}

/**
 * The ⋯ menu at the end of a list row (Admin_Panel_Wireframe.html, D4): safe
 * actions first, then a separator, then destructive ones in red. A disabled
 * action stays listed with the reason under it, rather than vanishing and
 * leaving the user wondering where it went.
 */
export function RowActionsMenu({ actions, label = 'Row actions', className }: { actions: RowAction[]; label?: string; className?: string }) {
  const safe = actions.filter((a) => !a.danger);
  const danger = actions.filter((a) => a.danger);
  const item = (a: RowAction) => (
    <DropdownMenuItem
      key={a.label}
      disabled={a.disabled}
      onSelect={() => a.onSelect()}
      className={cn('flex-col items-start gap-0', a.danger && !a.disabled && 'text-danger focus:text-danger')}
    >
      <span>{a.label}</span>
      {a.hint && <span className="text-[11px] font-normal text-muted-foreground">{a.hint}</span>}
    </DropdownMenuItem>
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn('inline-flex h-8 w-8 items-center justify-center rounded-md border-0 bg-transparent p-0 text-muted-foreground transition-colors hover:bg-muted hover:text-ink', className)}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px]">
        {safe.map(item)}
        {safe.length > 0 && danger.length > 0 && <DropdownMenuSeparator />}
        {danger.map(item)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
